import { query } from 'utils/graphql';
import { formatPageBuilderLastSaved } from '../../../../modules/datetime';
import {
  SITE_BRANDING_TAB,
  FACILITY_BRANDING_TAB,
} from '../../../../admin/components/BrandingModal/BrandingModal';
import {
  SWITCH_LIVE_SUCCESS as PAGE_SWITCH_LIVE_SUCCESS,
  LIVE_SUCCESS as PAGE_LIVE_SUCCESS,
  DRAFT_SUCCESS as PAGE_DRAFT_SUCCESS,
  makeLiveMajorVersions,
} from './pages';

const PAGE = 'page';
const LAYOUT = 'layout';

// Actions
const LOAD_PAGE = 'voyager/pageBuilder/LOAD_PAGE';
const LOAD_PAGE_SUCCESS = 'voyager/pageBuilder/LOAD_PAGE_SUCCESS';
const LOAD_PAGE_FAIL = 'voyager/pageBuilder/LOAD_FAIL';
const SAVE = 'voyager/pageBuilder/SAVE';
export const SAVE_SUCCESS = 'voyager/pageBuilder/SAVE_SUCCESS';
const SAVE_FAIL = 'voyager/pageBuilder/SAVE_FAIL';
const LIVE = 'voyager/pageBuilder/LIVE';
const LIVE_SUCCESS = 'voyager/pageBuilder/LIVE_SUCCESS';
const LIVE_FAIL = 'voyager/pageBuilder/LIVE_FAIL';
const REVISE = 'voyager/pageBuilder/REVISE';
const PUBLISH = 'voyager/pageBuilder/PUBLISH';
const PUBLISH_SUCCESS = 'voyager/pageBuilder/PUBLISH_SUCCESS';
const PUBLISH_FAIL = 'voyager/pageBuilder/PUBLISH_FAIL';
const SWITCH_VERSION = 'voyager/pageBuilder/SWITCH_VERSION';
const SWITCH_MAJOR_VERSION = 'voyager/pageBuilder/SWITCH_MAJOR_VERSION';
const OPEN_STYLES_MODAL = 'voyager/builder/OPEN_STYLES_MODAL';
const CLOSE_STYLES_MODAL = 'voyager/builder/CLOSE_STYLES_MODAL';

// Reducer
const initialState = {
  loading: false,
  loaded: false,
  saving: false,
  saveError: null,
  isPublishing: false,
  lastSaved: null,
  version: {},
  majorVersionsArray: [],
  showStyleModalTab: null,
  facilityId: null,
  selectedMajorVersion: null,
  restoreMode: false,
};

export default function reducer(state = initialState, action = {}) {
  switch (action.type) {
    case LOAD_PAGE:
      return {
        ...state,
        loading: true,
        loaded: false,
      };
    case LOAD_PAGE_SUCCESS:
      return {
        ...state,
        loading: false,
        loaded: true,
        type: PAGE,
        majorVersionsArray: action.data.page.majorVersionsArray || [], // Ensure it's populated
        ...action.data.page,
      };
    case LOAD_PAGE_FAIL:
      return {
        ...state,
        loading: false,
        loaded: false,
      };
    case REVISE:
      return {
        ...state,
        version: action.version,
        versions: [action.version, ...state.versions],
      };
    case SAVE:
    case LIVE:
      return {
        ...state,
        saving: true,
        saveError: null,
      };
    case PUBLISH:
      return {
        ...state,
        saving: true,
        isPublishing: true,
        saveError: null,
      };
    case SAVE_SUCCESS:
      return {
        ...state,
        restoreMode: false,
        saving: false,
        saveError: null,
        lastSaved: formatPageBuilderLastSaved(),
      };
    case SAVE_FAIL:
    case LIVE_FAIL:
      return {
        ...state,
        saving: false,
        saveError: action.error.message,
      };
    case PUBLISH_FAIL:
      return {
        ...state,
        saving: false,
        isPublishing: false,
        saveError: action.error.message,
      };
    case LIVE_SUCCESS:
      return {
        ...state,
        saving: false,
        loading: false,
        loaded: true,
        ...action.data.page,
      };
    case SWITCH_MAJOR_VERSION: {
      const { majorVersionId } = action;

      const majorVersionsArray = state.majorVersionsArray || [];
      const selectedMajorVersion = majorVersionsArray.find(
        majorVersion => majorVersion.id === majorVersionId,
      );

      if (!selectedMajorVersion || !selectedMajorVersion.versions?.length) {
        return {
          error: 'Selected major version has no sub-versions',
        };
      }

      // Check if the selected major version is the published version
      const isPublishedVersion = selectedMajorVersion.isPublished;

      return {
        ...state,
        restoreMode: !isPublishedVersion, // Set restoreMode to false if it's the published version
        selectedMajorVersion, // Track the selected major version
        version: selectedMajorVersion.versions[0], // Update to the first sub-version
      };
    }
    case PUBLISH_SUCCESS:
      return {
        ...state,
        saving: false,
        isPublishing: false,
        loading: false,
        loaded: true,
        restoreMode: false,
        selectedMajorVersion: null,
        majorVersionsArray: action.data.page.majorVersionsArray || [], // Ensure the array is updated
        ...action.data.page,
      };
    case SWITCH_VERSION:
      return {
        ...state,
        version: state.versions[action.index],
      };
    case PAGE_SWITCH_LIVE_SUCCESS:
      return {
        ...state,
        ...(state.id === action.data.forLive.id ? {
          live: true,
        } : {}),
        ...(state.id === action.data.forDraft.id ? {
          live: false,
        } : {}),
      };
    case PAGE_LIVE_SUCCESS:
      return {
        ...state,
        ...(state.id === action.data.page.id ? {
          live: true,
          publishedVersion: action.data.page.publishedVersion,
          majorVersionsArray: action.data.page.majorVersionsArray,
        } : {}),
      };
    case PAGE_DRAFT_SUCCESS:
      return {
        ...state,
        ...(state.id === action.data.page.id ? {
          live: false,
        } : {}),
      };
    case OPEN_STYLES_MODAL:
      return {
        ...state,
        showStyleModalTab: action.tab,
        facilityId: action.facilityId,
        widgetSwitchingId: null,
      };
    case CLOSE_STYLES_MODAL:
      return {
        ...state,
        showStyleModalTab: null,
        widgetSwitchingId: null,
      };
    default:
      return state;
  }
}
// NOTE: `pageBuilder.versions` is treated like a stack, so the version at the 0th
// position is the top and the version at `pageBuilder.versions.length - 1` is the
// bottom. Therefore, all next/previous positions are inverse.

export const getCurrentVersion = state => (
  state.pageBuilder.version
);

export const getLayout = state => (
  state.pageBuilder && state.pageBuilder.layout
);

export const getLayoutVersion = state => (
  getLayout(state) && state.pageBuilder.layout.publishedVersion
);

export const getPublishedVersion = state => state.pageBuilder.publishedVersion;

export const getRestoreMode = state => state.pageBuilder.restoreMode;

export const getPageVersions = state => (
  state.pageBuilder.versions || []
);

export const getSelectedMajorVersion = (state) => {
  if (state.pageBuilder && state.pageBuilder.selectedMajorVersion) {
    return state.pageBuilder.selectedMajorVersion;
  }
  return null;
};


export const getVersion = (state) => {
  if (state.pageBuilder && state.pageBuilder.version) {
    return state.pageBuilder.version;
  }
  return {};
};


export const getMajorVersions = state =>
  state.pageBuilder.majorVersionsArray || [];

export const currentVersionIsPublished = state => (
  !!state.pageBuilder.publishedVersion &&
  state.pageBuilder.version.id === state.pageBuilder.publishedVersion.id
);

export const hasNextVersion = state => (
  getCurrentVersionPosition(state) !== 0
);

export const hasPreviousVersion = (state) => {
  const versions = getPageVersions(state);

  return getCurrentVersionPosition(state) !== versions.length - 1;
};

export const getCurrentVersionPosition = (state) => {
  const versions = getPageVersions(state);
  const currentVersion = getCurrentVersion(state);

  return versions.findIndex(version => version.id === currentVersion.id);
};

export const getPreviousVersionPosition = (state) => {
  const currentPosition = getCurrentVersionPosition(state);

  if (!hasPreviousVersion(state)) {
    return undefined;
  }

  return currentPosition + 1;
};

export const getNextVersionPosition = (state) => {
  const currentPosition = getCurrentVersionPosition(state);

  if (!hasNextVersion(state)) {
    return undefined;
  }

  return currentPosition - 1;
};

export const getPageFacility = state => (
  state.pageBuilder.facility
);

export const getPageFacilityId = state => (
  state.pageBuilder && state.pageBuilder.facilityId
);

export const getId = state => (
  state.pageBuilder && state.pageBuilder.id
);

export const isPublishing = state => (
  state.pageBuilder.isPublishing
);

export const isSavingCurrentPageOrLayout = state => (
  state.pageBuilder.saving && !state.pageBuilder.isPublishing
);

export const hasFacility = state => (
  !!state.pageBuilder.facilityId
);

export const isPageLive = state => (
  state.pageBuilder.live
);

export const isEditingPage = state => (
  state.pageBuilder.type === PAGE
);

export const isEditingLayout = state => (
  state.pageBuilder && state.pageBuilder.type === LAYOUT
);

export const getLastSavedTime = state => (
  state.pageBuilder.lastSaved
);

export const getSubmissionStatus = state => (
  state.pageBuilder.saving
);

export const getPageLocale = state => (
  state && state.pageBuilder ? state.pageBuilder.locale : null
);

export const getPageType = state => (
  state.pageBuilder && state.pageBuilder.metadata && state.pageBuilder.metadata.type
);


export const getPageBuilder = state => (
  state.pageBuilder
);

export const isLoaded = (state, id) => (
  getId(state) === id && state.pageBuilder && state.pageBuilder.loaded
);

export const isSaving = (state, id) => (
  getId(state) === id && getSubmissionStatus(state)
);

export const showStyleModalTab = state => state.pageBuilder && state.pageBuilder.showStyleModalTab;

// Action Creators
export function openSiteStylesModal(facilityId) {
  return {
    type: OPEN_STYLES_MODAL,
    tab: SITE_BRANDING_TAB,
    facilityId,
  };
}

export function openFacilityStylesModal(facilityId) {
  return {
    type: OPEN_STYLES_MODAL,
    tab: FACILITY_BRANDING_TAB,
    facilityId,
  };
}

export function closeSiteStylesModal() {
  return {
    type: CLOSE_STYLES_MODAL,
  };
}

export function load(id) {
  return {
    types: [LOAD_PAGE, LOAD_PAGE_SUCCESS, LOAD_PAGE_FAIL],
    id,
    force: true,
    promise: query`
      query GetPage(${{ id }}: String!) {
        page(id: $id) {
          ...PageProps
        }
      }
      ${FragmentPageProps}
    `,
  };
}

export function save(page) {
  const toMutate = {
    id: page.id,
    facilityId: page.facilityId,
    version: page.version,
    metadata: page.metadata,
    disableEnUsPage: page.disableEnUsPage,
    customBreadcrumbs: page.customBreadcrumbs,
  };

  return {
    types: [SAVE, SAVE_SUCCESS, SAVE_FAIL],
    page,
    force: true,
    promise: query`
      mutation UpdatePage(${{ page: { ...toMutate } }}: PageInput!) {
        updatePage(page: $page) {
          id
        }
      }
    `,
  };
}

export function saveVersion(pageId, version) {
  return {
    types: [SAVE, SAVE_SUCCESS, SAVE_FAIL],
    force: true,
    id: pageId,
    version,
    promise: query`
      mutation PushPageVersion(${{ id: pageId }}: String!, ${{ version }}: PageVersionInput!) {
        pushPageVersion(id: $id, version: $version)
      }
    `,
  };
}

export function revise(version) {
  return {
    type: REVISE,
    version,
  };
}

export function publish(pageId, versionId) {
  return {
    types: [PUBLISH, PUBLISH_SUCCESS, PUBLISH_FAIL],
    force: true,
    promise: query`
      mutation PublishPageVersion(${{ id: pageId }}: String!, ${{ versionId }}: String!) {
        page: publishPageVersion(id: $id, versionId: $versionId) {
          ...PageProps
        }
      }
      ${FragmentPageProps}
    `,
  };
}

export function publishCurrent(id) {
  return (dispatch, getState) => {
    const state = getState();
    const currentVersion = getCurrentVersion(state);

    return dispatch(publish(id, currentVersion.id));
  };
}

export const switchVersion = index => ({
  type: SWITCH_VERSION,
  index,
});


export function publishMajorVersion(id, majorVersionId) {
  return async (dispatch) => {
    try {
      // Dispatch the major version switch action
      await dispatch({
        types: [PUBLISH, PUBLISH_SUCCESS, PUBLISH_FAIL],
        majorVersionId, // Pass the selected major version ID
        force: true,
        promise: query`
          mutation SwitchMajorVersion(${{ id }}: String!, ${{ majorVersionId }}: String!) {
            page: switchMajorVersion(id: $id, majorVersionId: $majorVersionId) {
              ...PageProps
            }
          }
          ${FragmentPageProps}
        `,
      });

      if (id) {
        dispatch(makeLiveMajorVersions(id));
      }
    } catch (error) {
      console.error('Error in publishMajorVersion:', error);
    }
  };
}


export function switchMajorVersion(pageId, majorVersionId) {
  return {
    type: SWITCH_MAJOR_VERSION, // Dispatch only the action type
    majorVersionId, // Pass the selected major version ID as payload
    pageId, // Include pageId if needed for handling in the reducer
  };
}


const FragmentVersionProps = `
  fragment VersionProps on PageVersion {
    id
    sections {
      id
      type
      data
      payload
      widgets {
        id
        type
        payload
        componentId
      }
    }
    styles
    createdAt
  }
`;

const FragmentVersionV2Props = `
  fragment VersionV2Props on Version {
    id
    sections {
      id
      type
      data
      payload
      widgets {
        id
        type
        payload
        componentId
      }
    }
    createdAt
  }
`;

const FragmentPageProps = `
  fragment PageProps on Page {
    id
    companyId
    siteId
    facilityId
    facility {
      primaryColor
      secondaryColor
      tertiaryColor
      socialMediaPlatforms {
        link
        provider
        enabled
      }
      publicLogo {
        mediumId
        medium {
          id
          filename
          url
          placeholder
          srcSet
          sourceWidth
          sourceHeight
          trash
          defaultMetadata {
            title
            altText
          }
          metadata {
            title
            altText
          }
        }
        alt
        link {
          href
          target
        }
        path
        title
        url
      }
    }
    disableEnUsPage
    metadata {
      slug
      type
      title
      internalPageName
      description
      titlePostfix
      noIndex
      customUrl
      canonical
    }
    boilerplate
    parent
    parentPage {
      id
      locale
      children {
        pageId
        lastUpdated
        lastPublished
        locale
        status {
          id
          context
          text
        }
      }
    }
    children {
      pageId
      lastUpdated
      lastPublished
      unpublishedChanges
      locale
      status {
        id
        context
        text
      }
    }
    childPageIds
    locale
    availableLanguages {
      locale
      path
    }
    live
    versions {
      ...VersionProps
    }
    version {
      ...VersionProps
    }
    publishedVersion {
      ...VersionProps
    }
    majorVersionsArray {
      id
      majorVersionNumber
      changedBy
      versionName
      majorVersionNumber
      versions {
        ...VersionProps
      }
      isPublished
      updatedAt
    }
    layout {
      id
      publishedVersion {
        ...VersionV2Props
      }
    }
    locale
  }
  ${FragmentVersionProps}
  ${FragmentVersionV2Props}
`;
